import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', warning: '#ff9800', error: '#f44336' };

function RuleManager({ user, onClose }) {
  const [rules, setRules] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    condition_type: 'from_email',
    condition_value: '',
    action_value: ''
  });

  useEffect(() => {
    fetchRules();
    fetchLabels();
  }, []);

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/emails/rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data.rules || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching rules:', error);
      setLoading(false);
    }
  };

  const fetchLabels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/emails/labels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLabels(response.data.labels || []);
    } catch (error) {
      console.error('Error fetching labels:', error);
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    if (!newRule.name || !newRule.condition_value || !newRule.action_value) {
      alert('Please fill all fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/emails/rules`, newRule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewRule({
        name: '',
        condition_type: 'from_email',
        condition_value: '',
        action_value: ''
      });
      setShowCreateForm(false);
      fetchRules();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating rule');
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/emails/rules/${ruleId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRules();
    } catch (error) {
      alert('Error toggling rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (window.confirm('Delete this rule?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/emails/rules/${ruleId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchRules();
      } catch (error) {
        alert('Error deleting rule');
      }
    }
  };

  const conditionTypes = [
    { value: 'from_email', label: 'From Email Address', placeholder: 'boss@company.com' },
    { value: 'from_domain', label: 'From Domain', placeholder: '@company.com' },
    { value: 'subject_contains', label: 'Subject Contains', placeholder: 'invoice' },
    { value: 'body_contains', label: 'Body Contains', placeholder: 'urgent' },
  ];

  const getConditionLabel = (type) => {
    return conditionTypes.find(c => c.value === type)?.label || type;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-2xl font-bold" style={{ color: COLORS.primary }}>⚙️ Auto-Folder Rules</h2>
          <button onClick={onClose} className="text-3xl text-gray-500 hover:text-gray-700">×</button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Info Banner */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              🤖 Auto-rules automatically organize your emails by applying labels based on sender, subject, or content.
            </p>
          </div>

          {/* Create Button */}
          {!showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="mb-4 px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
              style={{ backgroundColor: COLORS.primary }}
            >
              ➕ Create New Rule
            </button>
          )}

          {/* Create Form */}
          {showCreateForm && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-300">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Create New Rule</h3>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
              <form onSubmit={handleCreateRule} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                  <input
                    type="text"
                    value={newRule.name}
                    onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                    placeholder="e.g., Boss Emails → Important"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">When email...</label>
                  <select
                    value={newRule.condition_type}
                    onChange={(e) => setNewRule({ ...newRule, condition_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  >
                    {conditionTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                  <input
                    type="text"
                    value={newRule.condition_value}
                    onChange={(e) => setNewRule({ ...newRule, condition_value: e.target.value })}
                    placeholder={conditionTypes.find(c => c.value === newRule.condition_type)?.placeholder}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Apply Label</label>
                  <select
                    value={newRule.action_value}
                    onChange={(e) => setNewRule({ ...newRule, action_value: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  >
                    <option value="">-- Select Label --</option>
                    {labels.map((label) => (
                      <option key={label.id} value={label.name}>{label.name}</option>
                    ))}
                  </select>
                  {labels.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">Create labels first in Label Manager</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                  style={{ backgroundColor: COLORS.success }}
                >
                  ✨ Create Rule
                </button>
              </form>
            </div>
          )}

          {/* Existing Rules */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Your Rules ({rules.length})</h3>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : rules.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No rules yet. Create your first auto-rule above!
              </div>
            ) : (
              <div className="space-y-3">
                {rules.map((rule) => (
                  <div
                    key={rule.id}
                    className={`p-4 border rounded-lg ${
                      rule.is_active ? 'bg-white border-gray-300' : 'bg-gray-50 border-gray-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-gray-800">{rule.name}</h4>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            rule.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {rule.is_active ? '✓ Active' : '✗ Inactive'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>
                            <span className="font-medium">Condition:</span>{' '}
                            {getConditionLabel(rule.condition_type)} = "{rule.condition_value}"
                          </p>
                          <p>
                            <span className="font-medium">Action:</span>{' '}
                            Apply label "{rule.action_value}"
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <button
                          onClick={() => handleToggleRule(rule.id)}
                          className={`px-3 py-1 text-sm rounded ${
                            rule.is_active ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {rule.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDeleteRule(rule.id)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RuleManager;
